export function isMarkdownArtifact(name: string, mimeType: string) {
  return (
    mimeType.toLowerCase() === "text/markdown" ||
    mimeType.toLowerCase() === "text/x-markdown" ||
    /\.(md|markdown)$/i.test(name)
  );
}

export const MAX_MARKDOWN_PREVIEW_BYTES = 1024 * 1024;

/**
 * Chat tool results only embed a truncated excerpt of large text artifacts.
 * Comparing the excerpt against the stored artifact size tells the preview
 * reader whether it still needs to fetch the complete file.
 */
export function artifactPreviewIsComplete(
  content: string,
  sizeBytes: number | undefined,
) {
  if (!content) return false;
  if (
    typeof sizeBytes !== "number" ||
    !Number.isFinite(sizeBytes) ||
    sizeBytes <= 0
  )
    return false;
  // UTF-8 never encodes a code unit into fewer than one byte, so this
  // short-circuits the common complete-preview case without copying the text.
  if (content.length >= sizeBytes) return true;
  return new TextEncoder().encode(content).length >= sizeBytes;
}

export type ArtifactPreviewError =
  "unauthenticated" | "forbidden" | "not-found" | "too-large" | "unavailable";

/** Tracks whether the reader still needs the complete artifact file. */
export type ArtifactFullContentState = "idle" | "pending" | "done";

/**
 * Aborting an in-flight full-content load must return the reader to "idle"
 * (retryable) rather than leaving it stuck in "pending", which would stop a
 * later expand from fetching the complete artifact.
 */
export function releaseFullContentLoad(state: ArtifactFullContentState) {
  return state === "pending" ? ("idle" as const) : state;
}

export function safeArtifactUrl(value: string) {
  if (!value) return "";
  try {
    const origin =
      typeof window === "undefined"
        ? "http://localhost"
        : window.location.origin;
    const url = new URL(value, origin);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "";
    if (url.origin !== origin) return "";
    return url.href.startsWith(origin)
      ? `${url.pathname}${url.search}${url.hash}`
      : "";
  } catch {
    return "";
  }
}

export function artifactDownloadUrl(url: string, downloadUrl?: string) {
  const explicit = safeArtifactUrl(downloadUrl || "");
  if (explicit) return explicit;
  const safeUrl = safeArtifactUrl(url);
  if (!safeUrl) return "";
  const origin =
    typeof window === "undefined" ? "http://localhost" : window.location.origin;
  const parsed = new URL(safeUrl, origin);
  parsed.searchParams.set("download", "1");
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export async function loadArtifactPreview(
  url: string,
  fetcher: typeof fetch = fetch,
) {
  const safeUrl = safeArtifactUrl(url);
  if (!safeUrl) throw "unavailable" satisfies ArtifactPreviewError;
  const response = await fetcher(safeUrl, {
    credentials: "include",
    headers: { accept: "text/markdown, text/plain;q=0.9" },
  });
  if (!response.ok) throw artifactPreviewError(response.status);
  const length = Number(response.headers.get("content-length"));
  if (Number.isFinite(length) && length > MAX_MARKDOWN_PREVIEW_BYTES)
    throw "too-large" satisfies ArtifactPreviewError;
  const value = await response.text();
  if (value.length > MAX_MARKDOWN_PREVIEW_BYTES)
    throw "too-large" satisfies ArtifactPreviewError;
  return value;
}

export function artifactPreviewError(status: number): ArtifactPreviewError {
  if (status === 401) return "unauthenticated";
  if (status === 403) return "forbidden";
  if (status === 404) return "not-found";
  if (status === 413) return "too-large";
  return "unavailable";
}

export function previewErrorMessage(error: ArtifactPreviewError) {
  switch (error) {
    case "unauthenticated":
      return "Your session expired. Sign in again to preview this artifact.";
    case "forbidden":
      return "You do not have access to this artifact in the current account or organization.";
    case "not-found":
      return "This artifact is no longer available in the current account or organization.";
    case "too-large":
      return "This Markdown file is too large to preview safely. Download it to view the full file.";
    default:
      return "Preview unavailable. Open or download the file to view it.";
  }
}

export function artifactVaultErrorMessage(cause: unknown, fallback: string) {
  const status =
    cause && typeof cause === "object" && "status" in cause
      ? (cause as { status?: unknown }).status
      : undefined;
  if (status === 401)
    return "Your session expired. Sign in again to access your artifact vault.";
  if (status === 403)
    return "You do not have access to the artifact vault for the current account or organization.";
  if (status === 404)
    return "This artifact is no longer available in the current account or organization.";
  return cause instanceof Error ? cause.message : fallback;
}
